import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Phone, Users, Crown, ChevronDown, ChevronUp, ChevronRight, ArrowRight,
  Sparkles, Check, Spade, Diamond, Club,
  Car, Truck, Building2, ShieldCheck, Award, Star, Wifi, GlassWater, Briefcase, Baby, MapPin, Clock, Heart, Music,
  Plane, PartyPopper, Film, FileText,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => `$${Number(n).toLocaleString()}`;

/* ─────────── DATA: TIERS + FLEET (combined) ─────────── */
const TIERS = [
  {
    key: 'concierge',
    label: 'Concierge Driven',
    suit: Spade,
    color: 'from-gray-800 to-black',
    minHrs: 'No minimum',
    desc: 'Professional chauffeur in luxury sedans & SUVs',
    features: ['Professional chauffeur', 'Airport meet & greet', 'Flight tracking', 'Corporate accounts'],
    fleet: [
      { name: 'Luxury Sedan', models: 'CT6 · Model Y · Model X', pax: 'Up to 4', rate: 65 },
      { name: 'Premium Sedan', models: 'S-Class · BMW 7 Series', pax: 'Up to 4', rate: 95 },
      { name: 'Luxury SUV', models: 'Escalade · Yukon XL · Suburban', pax: 'Up to 6', rate: 100 },
      { name: 'Premium SUV', models: 'Escalade ESV · Yukon XL · Suburban (2025)', pax: 'Up to 6', rate: 125 },
    ],
  },
  {
    key: 'group',
    label: 'Group Driven',
    suit: Club,
    color: 'from-gray-700 to-gray-900',
    minHrs: '4-hr minimum',
    desc: 'Sprinter vans, limos, and buses for any group size',
    features: ['8–37 passengers', 'Conference tables', 'PA systems', 'Wedding parties'],
    fleet: [
      { name: 'Basic Shuttle', models: 'Sprinter', pax: 'Up to 12', rate: 125 },
      { name: 'Luxury Shuttle', models: 'Sprinter', pax: 'Up to 14', rate: 200 },
      { name: 'Luxury Limo Style', models: 'Sprinter', pax: 'Up to 13', rate: 200 },
      { name: 'Executive Van', models: 'Sprinter', pax: 'Up to 8', rate: 200 },
      { name: 'Executive Van XL', models: 'Sprinter', pax: 'Up to 10', rate: 250 },
      { name: 'Lincoln Stretch', models: 'Limousine', pax: '8–10', rate: 200 },
      { name: 'Hummer Stretch', models: 'Limousine', pax: '14–18', rate: 250 },
      { name: 'Executive Mini Bus', models: 'Coach', pax: '20–27', rate: 250 },
      { name: 'Executive Bus', models: 'Coach', pax: '37', rate: 275 },
    ],
  },
  {
    key: 'exotic',
    label: 'Exotic Driven',
    suit: Diamond,
    color: 'from-red-800 to-red-950',
    minHrs: '4-hr minimum',
    desc: 'Bentley, Lamborghini, Maybach, Rolls-Royce — chauffeured',
    features: ['Hand-stitched interiors', 'Starlight headliners', 'Champagne coolers', 'Content-creation ready'],
    isDark: true,
    fleet: [
      { name: 'Bentley Flying Spur', models: '', pax: 'VIP · 3–4', rate: 200 },
      { name: 'Maybach S600', models: '', pax: 'VIP · 3', rate: 200 },
      { name: 'Bentley Bentayga', models: '', pax: 'VIP · 4–5', rate: 250 },
      { name: 'Rolls-Royce Ghost', models: '', pax: 'VIP · 3', rate: 250 },
      { name: 'Maybach GLS600', models: '', pax: 'VIP · 4', rate: 300 },
      { name: 'Maybach S580 (2023)', models: '', pax: 'VIP · 3', rate: 350 },
      { name: 'Lamborghini Urus', models: '', pax: 'VIP · 4', rate: 350 },
      { name: 'Rolls-Royce Cullinan', models: '', pax: 'VIP · 4', rate: 450 },
    ],
  },
];

/* ─────────── DATA: VEHICLE TYPES ─────────── */
const VEHICLE_TYPES = [
  { icon: Car, type: 'Executive Sedan', cap: '1–3 Passengers · From $85/trip', desc: 'Refined, quiet, and effortlessly professional. The ideal solo executive ride — or an intimate couple\'s night out.', uses: ['Airport Transfers', 'Executive Daily', 'Date Night', 'Solo VIP', 'Spa Days'] },
  { icon: Truck, type: 'Luxury SUV', cap: '3–6 Passengers · From $135/trip', desc: 'Commanding presence. Room for luggage. Comfortable for families, small groups, or executives who prefer a bigger footprint.', uses: ['Airport Groups', 'Client Hosting', 'Weddings', 'Golf Runs', 'Shopping', 'Galas'] },
  { icon: Users, type: 'Sprinter Van', cap: '8–14 Passengers · From $225/trip', desc: 'The group move of choice. Spacious, flexible, and powerful — configured for executive transport or elevated social occasions.', uses: ['Bachelorette', 'Birthday Crawls', 'Wine Tours', 'Corporate Shuttle', 'Game Day'] },
  { icon: PartyPopper, type: 'Sprinter Lounge', cap: '10–14 Passengers · Custom Quote', desc: 'The party starts on board. Lounge-configured Sprinters with premium seating, lighting, and the vibe dialed all the way up.', uses: ['Birthday Parties', 'Bachelorette', 'Celebrations', 'Bar Crawls', 'Prom Night'] },
  { icon: Building2, type: 'Multi-Vehicle Fleet', cap: '15–100+ Guests · Custom Quote', desc: 'Weddings, conventions, major events — ACE coordinates multiple vehicles under one booking, one contact, one seamless plan.', uses: ['Weddings', 'Conferences', 'Reunions', 'Film Production', 'VIP Events'] },
  { icon: Plane, type: 'Tarmac & FBO Runs', cap: 'Private Aviation · From $175/trip', desc: 'Private terminal pickups, charter flight connections, and jet-setters who expect their ground transport to match the altitude.', uses: ['Private Aviation', 'FBO Pickup', 'Hangar Drop-Off', 'Exec Road Warrior'] },
];

const DrivenServices = () => {
  const navigate = useNavigate();
  const [openTier, setOpenTier] = useState({ concierge: true, group: false, exotic: false });

  const toggleTier = (k) => setOpenTier(p => ({ ...p, [k]: !p[k] }));

  return (
    <div className="min-h-screen bg-white" data-testid="driven-services-page">

      {/* HERO */}
      <section className="relative bg-black text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_30%,rgba(220,38,38,0.18),transparent_60%)]" />
        <div className="absolute -top-12 -right-12 text-[280px] sm:text-[420px] leading-none text-white/[0.02] select-none pointer-events-none">♠</div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 sm:py-32 lg:py-40">
          <p className="text-[11px] sm:text-xs text-red-500 font-bold tracking-[0.25em] mb-5">AUTO CONCIERGE ENTERPRISES · CONCIERGE DRIVEN</p>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black leading-[1.05] tracking-tight max-w-4xl" data-testid="driven-hero-title">
            Every Moment<br />Deserves a <em className="not-italic text-red-500">Driver.</em>
          </h1>
          <p className="text-base sm:text-lg text-gray-300 mt-6 max-w-2xl leading-relaxed">
            Whether it's a runway arrival, a rooftop dinner, or a weekend with the crew — ACE puts you in the back seat so you can be fully present for what matters.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button onClick={() => document.getElementById('occasions')?.scrollIntoView({ behavior: 'smooth' })} className="h-12 sm:h-14 px-6 sm:px-8 bg-red-600 hover:bg-red-700 text-white text-sm sm:text-base font-semibold" data-testid="hero-explore-btn">
              Explore How We Ride <ChevronRight className="ml-1.5 h-5 w-5" />
            </Button>
            <Button onClick={() => navigate('/booking')} variant="outline" className="h-12 sm:h-14 px-6 sm:px-8 border-white text-white hover:bg-white hover:text-black text-sm sm:text-base bg-transparent" data-testid="hero-book-btn">
              Book Your Ride
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-4 sm:gap-10 mt-12 sm:mt-16 max-w-2xl border-t border-white/10 pt-8">
            <div><p className="text-3xl sm:text-5xl font-black text-white">30+</p><p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-widest mt-1">Vehicles in Fleet</p></div>
            <div><p className="text-3xl sm:text-5xl font-black text-white">48+</p><p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-widest mt-1">Occasions Served</p></div>
            <div><p className="text-3xl sm:text-5xl font-black text-red-500">ATL</p><p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-widest mt-1">Based · Nationwide</p></div>
          </div>
        </div>
      </section>

      {/* INTRO STRIP */}
      <div className="bg-white border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-10 text-center">
          <p className="text-base sm:text-xl text-gray-700 italic font-light leading-relaxed">
            You don't need a special reason to ride in style. You just need to know <span className="text-red-600 font-semibold not-italic">what's possible</span> — and let ACE handle the rest.
          </p>
        </div>
      </div>

      {/* THE ACE STANDARD — 3 columns: Chauffeur Standards · Add-Ons & Preferences · What Sets Us Apart */}
      <section className="py-16 sm:py-24" data-testid="ace-standard-section">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-[11px] text-red-600 font-bold tracking-[0.25em] mb-3">THE ACE STANDARD</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] max-w-3xl">
            What Makes ACE <em className="not-italic text-red-500">Different.</em>
          </h2>
          <p className="text-sm sm:text-base text-gray-500 mt-4 max-w-2xl leading-relaxed">
            Anyone can give you a ride. Few can give you an experience. Here's what concierge actually means at ACE — the standards we hold every chauffeur to, the extras you can request, and the small things that separate us from the rest of Atlanta's car service market.
          </p>

          <div className="grid lg:grid-cols-3 gap-5 mt-10">
            {/* 1. Chauffeur Standards */}
            <Card className="p-7 border-gray-200 hover:border-red-200 transition-all" data-testid="ace-standard-chauffeur">
              <div className="flex items-center gap-2 mb-5">
                <div className="h-10 w-10 rounded-lg bg-red-600 flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-white" />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-red-600">01 · Chauffeur Standards</p>
              </div>
              <h3 className="text-xl font-black text-black mb-4">The People Behind the Wheel</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-5">Every ACE chauffeur is hand-picked, vetted, and held to a uniform standard. We don't gig out our service — we built it.</p>
              <ul className="space-y-2.5 text-sm" data-testid="chauffeur-standards-list">
                {[
                  ['DOT-Compliant', 'TSA-verified background checks, fingerprinted, drug-screened'],
                  ['7-Year MVR Clean', 'Driving records reviewed annually'],
                  ['Defensive Driver Certified', 'Smith System / National Safety Council training'],
                  ['Suit & Tie Standard', 'Black-tie attire for all VIP service · custom looks on request'],
                  ['Discreet & Trained', 'NDAs signed · trained on protocol, etiquette, and silence when needed'],
                  ['Live GPS Tracking', 'Dispatch monitors every ride in real time · ETAs auto-shared with clients'],
                ].map(([title, desc]) => (
                  <li key={title} className="flex gap-2.5">
                    <Check className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-black text-[13px]">{title}</p>
                      <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            {/* 2. Add-Ons & Preferences */}
            <Card className="p-7 border-gray-200 hover:border-red-200 transition-all" data-testid="ace-standard-addons">
              <div className="flex items-center gap-2 mb-5">
                <div className="h-10 w-10 rounded-lg bg-red-600 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-red-600">02 · Add-Ons & Preferences</p>
              </div>
              <h3 className="text-xl font-black text-black mb-4">Customize Your Ride</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-5">Add the details that make a ride yours. Most extras are complimentary or modest — just ask at the time of booking.</p>
              <div className="grid grid-cols-2 gap-2.5 text-sm" data-testid="addons-grid">
                {[
                  { icon: GlassWater, title: 'Champagne / Bar', desc: 'Premium pour for celebrations' },
                  { icon: Heart, title: 'Bottled Water', desc: 'Iced & ready · Always free' },
                  { icon: Briefcase, title: 'Airport Signage', desc: 'Name placard at baggage claim' },
                  { icon: Baby, title: 'Child Seats', desc: 'Infant, booster, convertible' },
                  { icon: Wifi, title: 'Mobile WiFi Hotspot', desc: 'In-car LTE for productivity' },
                  { icon: MapPin, title: 'Custom Route Plan', desc: 'Specific stops · scenic routes' },
                  { icon: Music, title: 'Custom Playlist', desc: 'Spotify / Apple Music handoff' },
                  { icon: Phone, title: 'Phone Charger Kit', desc: 'iPhone & USB-C in every vehicle' },
                ].map((a) => (
                  <div key={a.title} className="p-2.5 bg-gray-50 rounded-lg border border-gray-100">
                    <a.icon className="h-4 w-4 text-red-600 mb-1.5" />
                    <p className="text-[11px] font-bold text-black leading-tight">{a.title}</p>
                    <p className="text-[10px] text-gray-500 leading-relaxed mt-0.5">{a.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-gray-400 mt-4 italic">Don't see what you need? Ask. We've coordinated everything from baby showers to private jets.</p>
            </Card>

            {/* 3. What Sets Us Apart */}
            <Card className="p-7 border-gray-200 hover:border-red-200 transition-all" data-testid="ace-standard-differentiators">
              <div className="flex items-center gap-2 mb-5">
                <div className="h-10 w-10 rounded-lg bg-red-600 flex items-center justify-center">
                  <Award className="h-5 w-5 text-white" />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-red-600">03 · What Sets Us Apart</p>
              </div>
              <h3 className="text-xl font-black text-black mb-4">Beyond Black-Car Average</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-5">The market is full of car services. Here's why ACE clients book us again — and refer us.</p>
              <ul className="space-y-3 text-sm" data-testid="differentiators-list">
                {[
                  { icon: Clock, title: 'On-Time, Every Time', desc: 'Guaranteed 15-minute window. If we\'re late, the next ride is on us.' },
                  { icon: Car, title: 'Owned Fleet, Not Subcontracted', desc: 'Every ACE vehicle is owned, branded, and maintained in-house — never an Uber Black flip.' },
                  { icon: Crown, title: 'Real Concierge, Not Just a Driver', desc: 'Dinner reservations, ticket pickups, surprise gifts — your chauffeur is empowered to handle the whole evening.' },
                  { icon: Star, title: '4.9★ from 800+ Atlanta Clients', desc: 'Five years serving the city · most consistent reviews in market.' },
                  { icon: Building2, title: 'Corporate Accounts & Net-30 Billing', desc: 'Standing reservations · monthly invoicing · single point of contact for finance.' },
                ].map((d) => (
                  <li key={d.title} className="flex gap-2.5">
                    <d.icon className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-black text-[13px]">{d.title}</p>
                      <p className="text-gray-500 text-xs leading-relaxed">{d.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* Teaser strip pointing to standalone Occasions page */}
          <div className="mt-12 p-6 sm:p-8 bg-gradient-to-r from-black via-gray-950 to-black rounded-2xl border border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-5" data-testid="occasions-teaser">
            <div className="text-center sm:text-left">
              <p className="text-[10px] uppercase tracking-widest text-red-500 font-bold mb-1">24+ Occasions · 4 Chapters</p>
              <h3 className="text-xl sm:text-2xl font-black text-white">Looking for a specific occasion?</h3>
              <p className="text-sm text-gray-400 mt-1">From wedding day to airport run — see every reason people book ACE.</p>
            </div>
            <Button onClick={() => navigate('/driven/occasions')} className="bg-red-600 hover:bg-red-700 text-white text-sm h-11 px-6 whitespace-nowrap" data-testid="see-all-occasions-btn">
              See All Occasions <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* COMBINED: Choose Your Experience + Browse by Category */}
      <section className="bg-gray-50 py-16 sm:py-24" id="tiers" data-testid="tiers-section">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-[11px] text-red-600 font-bold tracking-[0.25em] mb-3">CHOOSE YOUR EXPERIENCE · BROWSE BY CATEGORY</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] max-w-3xl">
            Three Tiers.<br /><em className="not-italic text-red-500">One Standard.</em>
          </h2>
          <p className="text-sm sm:text-base text-gray-500 mt-4 max-w-2xl">
            Every ACE vehicle is concierge-driven — you never drive. Pick a tier, then browse the full lineup with live rates.
          </p>

          <div className="grid lg:grid-cols-3 gap-5 mt-10">
            {TIERS.map((t) => {
              const Suit = t.suit;
              const isOpen = openTier[t.key];
              const minRate = Math.min(...t.fleet.map(v => v.rate));
              const maxRate = Math.max(...t.fleet.map(v => v.rate));
              return (
                <Card
                  key={t.key}
                  className={`overflow-hidden border-0 shadow-md transition-all ${t.isDark ? 'bg-gray-950 text-white' : 'bg-white border border-gray-200'}`}
                  data-testid={`tier-card-${t.key}`}
                >
                  <div className={`h-2 bg-gradient-to-r ${t.color}`} />
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${t.color} flex items-center justify-center flex-shrink-0`}>
                        <Suit className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className={`text-lg font-bold ${t.isDark ? 'text-white' : 'text-black'}`}>{t.label}</h3>
                        <p className={`text-xs ${t.isDark ? 'text-gray-500' : 'text-gray-500'}`}>{t.minHrs}</p>
                      </div>
                    </div>
                    <p className={`text-sm mb-4 ${t.isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t.desc}</p>
                    <ul className="space-y-1.5 mb-5">
                      {t.features.map((f, i) => (
                        <li key={i} className={`flex items-center gap-2 text-sm ${t.isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          <Check className="h-3.5 w-3.5 text-red-500 flex-shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>

                    {/* Inline expandable fleet list */}
                    <button
                      onClick={() => toggleTier(t.key)}
                      className={`w-full flex items-center justify-between py-3 px-3 rounded-lg ${t.isDark ? 'bg-white/5 hover:bg-white/10 text-white' : 'bg-gray-50 hover:bg-gray-100 text-gray-900'} transition-colors`}
                      data-testid={`tier-toggle-${t.key}`}
                    >
                      <span className="text-xs font-bold uppercase tracking-wider">
                        {t.fleet.length} vehicles · {fmt(minRate)}–{fmt(maxRate)}/hr
                      </span>
                      {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {isOpen && (
                      <div className={`mt-2 ${t.isDark ? 'border-white/5' : 'border-gray-100'} border rounded-lg overflow-hidden`} data-testid={`tier-fleet-${t.key}`}>
                        {t.fleet.map((v, i) => (
                          <div key={i} className={`flex items-center px-3 py-2.5 ${i > 0 ? `border-t ${t.isDark ? 'border-white/5' : 'border-gray-100'}` : ''}`}>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-semibold truncate ${t.isDark ? 'text-white' : 'text-gray-900'}`}>{v.name}</p>
                              {v.models && <p className={`text-[10px] truncate ${t.isDark ? 'text-gray-500' : 'text-gray-400'}`}>{v.models}</p>}
                            </div>
                            <span className={`text-[10px] mx-2 hidden sm:inline ${t.isDark ? 'text-gray-500' : 'text-gray-400'}`}>{v.pax}</span>
                            <span className={`text-sm font-black ${t.isDark ? 'text-white' : 'text-gray-900'}`}>
                              {fmt(v.rate)}<span className={`text-[9px] font-normal ${t.isDark ? 'text-gray-500' : 'text-gray-400'}`}>/hr</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    <Button
                      onClick={() => navigate('/booking')}
                      className={`w-full mt-5 ${t.isDark ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-black hover:bg-gray-800 text-white'}`}
                      data-testid={`tier-book-${t.key}`}
                    >
                      Book {t.label}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Button onClick={() => navigate('/rates')} variant="outline" className="border-gray-300 text-sm" data-testid="view-full-rate-card">
              View Full Rate Card <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* FLEET STRIP */}
      <div className="bg-gradient-to-r from-red-700 to-red-900 text-white py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-4xl font-black tracking-tight">30+ Vehicles. One Call.</h2>
          <p className="text-sm sm:text-base text-red-100 mt-3 max-w-2xl mx-auto leading-relaxed">
            ACE has access to a full luxury fleet — from polished executive sedans to first-class Sprinter coaches. Scale up or down. We match the vehicle to the moment.
          </p>
          <button
            onClick={() => document.getElementById('vehicles')?.scrollIntoView({ behavior: 'smooth' })}
            className="mt-5 inline-flex items-center gap-2 text-sm font-bold border-b-2 border-white pb-1 hover:text-red-100"
            data-testid="see-fleet-anchor"
          >
            See the Fleet Lineup <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* VEHICLE TYPES */}
      <section className="py-16 sm:py-24" id="vehicles">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-[11px] text-red-600 font-bold tracking-[0.25em] mb-3">THE FLEET · WHAT RIDES WHERE</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] max-w-3xl">
            Right Vehicle.<br /><em className="not-italic text-red-500">Right Occasion.</em>
          </h2>
          <p className="text-sm sm:text-base text-gray-500 mt-4 max-w-2xl">
            Not sure what you need? Let this guide you. Each vehicle class is built for different experiences, group sizes, and energy levels.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {VEHICLE_TYPES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Card key={i} className="p-6 border-gray-200 hover:shadow-lg transition-all group" data-testid={`vehicle-card-${i}`}>
                  <div className="h-12 w-12 rounded-xl bg-black flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-base sm:text-lg font-bold text-black">{v.type}</p>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-red-600 mt-1">{v.cap}</p>
                  <p className="text-sm text-gray-600 mt-3 leading-relaxed">{v.desc}</p>
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {v.uses.map((u, ui) => (
                      <span key={ui} className="text-[10px] font-semibold bg-gray-100 text-gray-700 px-2 py-1 rounded-full">{u}</span>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRODUCTION SERVICES — featured strip */}
      <section className="bg-gradient-to-br from-gray-950 via-black to-gray-950 text-white py-16 sm:py-20 relative overflow-hidden" id="production" data-testid="production-section">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,_rgba(220,38,38,0.12),_transparent_55%)]" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-[11px] text-red-500 font-bold tracking-[0.3em] mb-3 flex items-center gap-2">
                <Film className="h-3.5 w-3.5" /> SPECIALIZED · PRODUCTION
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] mb-5">
                Production-Ready<br /><em className="not-italic text-red-500">Transport.</em>
              </h2>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed mb-6 max-w-lg">
                Music videos, photo shoots, commercials, stunt work, and on-set logistics. ACE understands production timelines, permit zones, talent privacy, and the rhythm of a working set. We've shuttled crews, hosted talent, and stood by as picture-vehicle stand-ins for studios across Atlanta.
              </p>
              <ul className="space-y-2.5 mb-7">
                {[
                  'Music video & commercial picture cars (Maybach · Rolls · Lambo · G-Wagon)',
                  'Talent runner & artist transport with NDA-signed chauffeurs',
                  'Multi-vehicle crew shuttles to set, hotel, and craft services',
                  'Photoshoot location vehicles (interior & exterior background)',
                  'Day-rate billing · weekly holds · production-friendly net-30',
                ].map((line, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-gray-300 leading-relaxed">
                    <Check className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => navigate('/intake/event-booking?ref=production')} className="bg-red-600 hover:bg-red-700 text-white h-11 text-sm" data-testid="production-request-btn">
                  Request Production Quote <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <a
                  href={`${API}/api/production-sheet.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center h-11 px-5 rounded-lg border border-red-600 text-white bg-red-600/10 hover:bg-red-600/20 text-sm font-bold"
                  data-testid="production-download-sheet-btn"
                >
                  <FileText className="h-4 w-4 mr-2" /> Download Sales Sheet (PDF)
                </a>
                <a
                  href={`mailto:JD@YOURAUTOCONCIERGE.COM?subject=${encodeURIComponent('Production Inquiry — ACE Auto Concierge')}&body=${encodeURIComponent(
                    `Hi ACE Production Desk,\n\nI'd like to discuss production transport for an upcoming project.\n\n` +
                    `Project type: \n` +
                    `Shoot dates: \n` +
                    `Location(s): \n` +
                    `Vehicles needed: \n` +
                    `Number of crew: \n` +
                    `Talent transport needed: \n\n` +
                    `For reference, the ACE for Productions sales sheet is here:\n` +
                    `${typeof window !== 'undefined' ? window.location.origin : ''}/api/production-sheet.pdf\n\n` +
                    `Thanks,`
                  )}`}
                  className="inline-flex items-center justify-center h-11 px-5 rounded-lg border border-gray-700 text-gray-200 hover:bg-white/5 text-sm font-bold"
                  data-testid="production-email-btn"
                >
                  Email Production Desk
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Music Videos', desc: 'Hero & background picture cars' },
                { label: 'Editorial Shoots', desc: 'Magazine, lookbook, brand campaigns' },
                { label: 'Talent Transport', desc: 'Discreet artist & crew runs' },
                { label: 'On-Set Logistics', desc: 'Multi-vehicle production shuttles' },
                { label: 'Commercials', desc: 'Branded picture-car standby' },
                { label: 'Brand Activations', desc: 'Pop-ups, launches, premieres' },
              ].map((c) => (
                <div key={c.label} className="p-4 bg-white/[0.04] border border-white/10 rounded-lg backdrop-blur-sm" data-testid={`prod-card-${c.label.replace(/\s+/g, '-').toLowerCase()}`}>
                  <p className="text-sm font-black mb-1">{c.label}</p>
                  <p className="text-[11px] text-gray-400 leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SEE ALL OCCASIONS LINK */}
      <section className="bg-gray-50 py-14 sm:py-20" id="all-occasions">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-[11px] text-red-600 font-bold tracking-[0.25em] mb-3">THE FULL PICTURE · EVERY OCCASION</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] mb-3">
            If You're Going,<br /><em className="not-italic text-red-500">ACE Can Drive.</em>
          </h2>
          <p className="text-sm sm:text-base text-gray-500 mt-3 max-w-2xl mx-auto leading-relaxed">
            From airport runs to weddings, from production shoots to spa days — see every occasion ACE supports in one searchable catalog.
          </p>
          <Button onClick={() => navigate('/driven/occasions')} className="mt-7 bg-black hover:bg-red-600 text-white text-sm h-11 px-7" data-testid="see-all-occasions-btn-2">
            Browse 50+ Occasions <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* BOOKING CTA */}
      <section className="bg-black text-white py-16 sm:py-24" id="book">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.1]">
            Your Moment Is<br /><em className="not-italic text-red-500">Already Waiting.</em>
          </h2>
          <p className="text-sm sm:text-base text-gray-400 mt-5 max-w-xl mx-auto leading-relaxed">
            Tell us where you're going, when you need to be there, and how many seats you need. ACE handles everything else.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Button onClick={() => navigate('/booking')} className="h-12 sm:h-14 px-6 sm:px-8 bg-red-600 hover:bg-red-700 text-white text-sm sm:text-base font-semibold" data-testid="bottom-reserve-btn">
              Reserve Your Ride <ChevronRight className="ml-1.5 h-5 w-5" />
            </Button>
            <Button onClick={() => navigate('/contact')} variant="outline" className="h-12 sm:h-14 px-6 sm:px-8 border-white text-white hover:bg-white hover:text-black text-sm sm:text-base bg-transparent" data-testid="bottom-quote-btn">
              Get a Custom Quote
            </Button>
            <a href="tel:6788463009"><Button variant="outline" className="h-12 sm:h-14 px-6 sm:px-8 border-white text-white hover:bg-white hover:text-black text-sm sm:text-base bg-transparent">
              <Phone className="mr-2 h-5 w-5" /> (678) 846-3009
            </Button></a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DrivenServices;
